import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Link2, Check, Loader2, Stethoscope, FlaskConical, Pill, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { abdmApi, consultationApi, bookingApi, pharmacyApi } from "../lib/api";
import { useAuthStore } from "../store/authStore";

const TYPE_META = {
  OPConsultation: { icon: Stethoscope, label: "Consultation", color: "text-sky-600 bg-sky-100" },
  DiagnosticReport: { icon: FlaskConical, label: "Lab / Diagnostic", color: "text-violet-600 bg-violet-100" },
  Prescription: { icon: Pill, label: "Prescription", color: "text-amber-600 bg-amber-100" },
};

export default function ShareAbha() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?._id;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [linkedMap, setLinkedMap] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [ccRes, consRes, bookRes, rxRes] = await Promise.allSettled([
        abdmApi.hipCareContexts(userId),
        consultationApi.listPatient(userId),
        bookingApi.all(userId),
        pharmacyApi.prescriptions(userId),
      ]);

      const map = {};
      if (ccRes.status === "fulfilled") {
        (ccRes.value.data.careContexts || []).forEach((c) => { map[c.recordId] = c.linked; });
      }
      setLinkedMap(map);

      const out = [];
      if (consRes.status === "fulfilled") {
        (consRes.value.data?.consultations || consRes.value.data || []).forEach((c) => {
          const id = c._id || c.id || c.booking_id;
          if (id) out.push({ record_id: `cons-${id}`, record_type: "OPConsultation",
            display: `${c.doctor_name || c.specialty || "Consultation"} · ${(c.scheduled_time || c.created_at || "").slice(0, 10)}` });
        });
      }
      if (bookRes.status === "fulfilled") {
        const bookings = bookRes.value.data?.bookings || bookRes.value.data || [];
        bookings.filter((b) => (b.type || b.booking_type || "").toLowerCase().includes("lab") || (b.lab_name || b.test_name))
          .forEach((b) => {
            const id = b._id || b.id || b.booking_id;
            if (id) out.push({ record_id: `lab-${id}`, record_type: "DiagnosticReport",
              display: `${b.test_name || b.lab_name || "Lab test"} · ${(b.booking_date || b.created_at || "").slice(0, 10)}` });
          });
      }
      if (rxRes.status === "fulfilled") {
        (rxRes.value.data?.prescriptions || rxRes.value.data || []).forEach((p) => {
          const id = p._id || p.id || p.prescription_id;
          if (id) out.push({ record_id: `rx-${id}`, record_type: "Prescription",
            display: `${p.title || p.doctor_name || "Prescription"} · ${(p.created_at || p.date || "").slice(0, 10)}` });
        });
      }
      setItems(out);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const link = async (item) => {
    setBusyId(item.record_id);
    try {
      const res = await abdmApi.hipLink({ patient_id: userId, record_type: item.record_type, record_id: item.record_id, display: item.display });
      setLinkedMap((m) => ({ ...m, [item.record_id]: res.data.linked ?? true }));
      toast.success("Linking to ABHA — it will appear in your health account shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link this record");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="share-abha-back"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="text-lg font-bold">Share Records with ABHA</h1>
      </div>

      <Card className="rounded-2xl p-3 flex items-start gap-2 bg-emerald-50 border-emerald-100" data-testid="share-abha-info">
        <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-800">Link a record to your ABHA so any ABDM hospital or clinic can securely access it with your consent.</p>
      </Card>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-2xl bg-muted animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl p-6 text-center" data-testid="share-abha-empty">
          <p className="text-sm text-muted-foreground">No consultations, lab reports or prescriptions yet. They will show here once you have records to share.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const meta = TYPE_META[item.record_type];
            const Icon = meta.icon;
            const linked = linkedMap[item.record_id];
            return (
              <Card key={item.record_id} className="rounded-2xl p-3 flex items-center gap-3" data-testid={`share-item-${item.record_id}`}>
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-muted-foreground">{meta.label}</p>
                  <p className="text-sm font-medium truncate">{item.display}</p>
                </div>
                {linked ? (
                  <span className="flex items-center gap-1 text-emerald-600 text-sm font-semibold" data-testid={`share-linked-${item.record_id}`}>
                    <Check className="h-4 w-4" /> Linked
                  </span>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-full shrink-0" disabled={busyId === item.record_id}
                    onClick={() => link(item)} data-testid={`share-link-${item.record_id}`}>
                    {busyId === item.record_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1" /> Link</>}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
